import { useState } from 'react';
import { useServerList } from '../hooks/useServerList';
import { normalizePagination } from '../lib/pagination';
import { Pagination } from '../components/common/Pagination';
import { formatCurrency } from '../lib/format';
import { useNavigate } from 'react-router-dom';
import {
  Package2,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  DollarSign,
  AlertCircle,
  Search,
  ChevronRight,
  FileText,
  ExternalLink
} from 'lucide-react';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { BulkOrderStatus } from '@bloom/shared';
import { formatDate as formatDateUtil } from '../utils/dateUtils';

type StatusFilter = 'all' | BulkOrderStatus;

const PAGE_SIZE = 25;

interface StatusConfig {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ComponentType<any>;
}

const statusConfig: Record<BulkOrderStatus, StatusConfig> = {
  draft: {
    label: 'Draft',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    icon: FileText
  },
  pending_approval: {
    label: 'Pending Approval',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    icon: Clock
  },
  awaiting_payment: {
    label: 'Awaiting Payment',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    icon: DollarSign
  },
  paid: {
    label: 'Paid',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    icon: CheckCircle
  },
  processing: {
    label: 'Processing',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    icon: Package2
  },
  shipped: {
    label: 'Shipped',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    icon: Truck
  },
  delivered: {
    label: 'Delivered',
    color: 'text-green-700',
    bgColor: 'bg-green-200',
    icon: CheckCircle
  },
  voided: {
    label: 'Voided',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    icon: XCircle
  },
  cancelled: {
    label: 'Cancelled',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    icon: XCircle
  }
};

export const BulkOrdersList: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();
  // ?status= preselects a filter (the dashboard's "Review Orders" links here).
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('status');
    return fromUrl && fromUrl in statusConfig ? (fromUrl as BulkOrderStatus) : 'all';
  });
  // Per-status counts over every order matching the search (from the server).
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  // Search and status filter run on the server over every bulk order; the
  // hook debounces typing and returns to page 1 on any change.
  const list = useServerList<any, { status: StatusFilter }>({
    pageSize: PAGE_SIZE,
    filters: { status: statusFilter },
    fetchPage: async ({ page, pageSize, search, filters, signal }) => {
      const response = await apiClient.getBulkOrders({
        status: filters.status === 'all' ? undefined : filters.status,
        search,
        page,
        limit: pageSize,
        signal,
      });
      if (!response.success) throw new Error(response.error || 'Failed to load bulk orders');
      const items = response.data || [];
      if (!signal.aborted && response.statusCounts) setStatusCounts(response.statusCounts);
      const p = normalizePagination(response.pagination, { page, pageSize, itemCount: items.length });
      return { items, total: p.total, hasMore: p.hasMore };
    },
  });
  const orders = list.items;
  // Nothing has answered yet counts as loading, so the empty state never flashes first.
  const loading = list.loading || (list.total === null && !list.error);
  const searchTerm = list.search;
  const allCount = Object.values(statusCounts).reduce((sum, n) => sum + n, 0);

  const pendingApprovalCount = statusCounts['pending_approval'] || 0;


  const formatDate = (date: string | Date) => formatDateUtil(date);

  const getStatusBadge = (status: BulkOrderStatus) => {
    const config = statusConfig[status];
    if (!config) return null;

    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Please log in to view bulk orders.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Package2 className="h-7 w-7 mr-2 text-blue-600" />
            Bulk Orders
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            View and manage all bulk orders
          </p>
        </div>
        <button
          onClick={() => navigate('/bulk-orders')}
          className="mt-4 sm:mt-0 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Package2 className="w-4 h-4" />
          New Bulk Order
        </button>
      </div>

      {/* Pending Approvals Alert for Physicians */}
      {user.role === 'physician' && pendingApprovalCount > 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-yellow-800">
                {pendingApprovalCount} Order{pendingApprovalCount > 1 ? 's' : ''} Awaiting Your Approval
              </h3>
              <p className="text-sm text-yellow-700">
                Your account manager has created orders that need your review and approval.
              </p>
            </div>
            <button
              onClick={() => setStatusFilter('pending_approval')}
              className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700"
            >
              View Orders
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order number or physician..."
                value={searchTerm}
                onChange={(e) => list.setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter Chips */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === 'all'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({allCount})
            </button>
            {user.role === 'physician' && (
              <button
                onClick={() => setStatusFilter('pending_approval')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  statusFilter === 'pending_approval'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                }`}
              >
                Pending Approval ({statusCounts['pending_approval'] || 0})
              </button>
            )}
            <button
              onClick={() => setStatusFilter('awaiting_payment')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === 'awaiting_payment'
                  ? 'bg-orange-600 text-white'
                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
            >
              Awaiting Payment ({statusCounts['awaiting_payment'] || 0})
            </button>
            <button
              onClick={() => setStatusFilter('shipped')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === 'shipped'
                  ? 'bg-purple-600 text-white'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              Shipped ({statusCounts['shipped'] || 0})
            </button>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-500">Loading orders...</p>
          </div>
        ) : list.error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-600">{list.error}</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="p-8 text-center">
            <Package2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {!searchTerm.trim() && statusFilter === 'all'
                ? 'No bulk orders yet'
                : 'No orders match your filters'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {orders.map((order) => (
              <div
                key={order._id}
                onClick={() => navigate(`/bulk-orders/${order._id}`)}
                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-medium text-gray-900">
                        {order.bulkOrderNumber}
                      </span>
                      {getStatusBadge(order.status)}
                    </div>

                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500">
                      {/* Physician info */}
                      {order.physicianId?.profile && (
                        <span>
                          Dr. {order.physicianId.profile.firstName} {order.physicianId.profile.lastName}
                        </span>
                      )}

                      {/* Items count */}
                      <span>{order.items?.length || 0} items</span>

                      {/* Total */}
                      <span className="font-medium text-gray-900">
                        {formatCurrency(order.totalAmount || 0)}
                      </span>

                      {/* Date */}
                      <span>{formatDate(order.createdAt)}</span>

                      {/* Tracking */}
                      {order.trackingInfo?.trackingNumber && (
                        order.trackingInfo.trackingUrl ? (
                          <a
                            href={order.trackingInfo.trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            <Truck className="w-3 h-3" />
                            Track: ...{order.trackingInfo.trackingNumber.slice(-6)}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-gray-600">
                            <Truck className="w-3 h-3" />
                            Tracking: ...{order.trackingInfo.trackingNumber.slice(-6)}
                          </span>
                        )
                      )}

                      {/* Created by (for physicians) */}
                      {user.role === 'physician' && order.createdById?._id !== user._id && (
                        <span className="text-blue-600">
                          Created by: {order.createdById?.profile?.firstName} {order.createdById?.profile?.lastName}
                        </span>
                      )}
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
        {list.totalPages !== null && (
          <Pagination
            currentPage={list.page}
            totalPages={list.totalPages}
            onPageChange={list.setPage}
            totalItems={list.total ?? undefined}
            itemsPerPage={PAGE_SIZE}
          />
        )}
      </div>
    </div>
  );
};

export default BulkOrdersList;
